import React from "react";
import { useTranslation } from "react-i18next";
import {
  ChevronLeft,
  Users,
  Bot,
  MessageCircle,
  ExternalLink,
  Calendar,
  MapPin,
} from "lucide-react";
import { Poll, SwipeVote } from "./utils/types";

interface PollHeaderProps {
  poll: Poll | null;
  existingVotes?: SwipeVote[];
  onBack?: () => void;
  totalVotes?: number;
  totalVoters?: number; // Ajouter le nombre de votants
  remainingVotes?: number;
  progressPercent?: number;
  onViewConversation?: (conversationId: string) => void;
}

export const PollHeader: React.FC<PollHeaderProps> = ({
  poll,
  existingVotes = [],
  onBack,
  totalVotes = 0,
  totalVoters = 0, // Utiliser la prop totalVoters
  remainingVotes = 0,
  progressPercent = 0,
  onViewConversation,
}) => {
  const { t, i18n } = useTranslation("polls");

  // Formater la date d'expiration
  const formatExpiryDate = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleDateString(i18n.language || "fr", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  // Récupérer la date de fin depuis les settings si disponible
  const expiryDate = React.useMemo(() => {
    // D'abord essayer depuis expires_at (ancien format)
    if (poll?.expires_at) return poll.expires_at;

    // Ensuite essayer depuis les settings (nouveau format)
    if ((poll as any)?.settings?.expiresAt)
      return (poll as any).settings.expiresAt;

    return "";
  }, [poll]);

  // Récupérer le lieu depuis les settings
  // La modale de pré-publication enregistre `location`, le formulaire de
  // réglages `eventLocation` : on affiche l'un ou l'autre.
  const eventLocation = React.useMemo(() => {
    const settings = (poll as any)?.settings;
    return settings?.eventLocation || settings?.location;
  }, [poll]);

  // Utiliser totalVoters passé en prop, ou calculer depuis existingVotes si non fourni
  const totalParticipants =
    totalVoters ||
    (existingVotes.length > 0
      ? new Set(existingVotes.map((v) => v.voter_email).filter(Boolean)).size
      : 0);

  return (
    <div className="p-4 relative bg-background border-b border-border">
      {/* Titre et description */}
      <div className="text-center">
        <h1 className="text-2xl sm:text-3xl font-bold text-foreground leading-tight">
          {poll?.title}
        </h1>
        {poll?.description && (
          <p
            className="mx-auto mt-3 max-w-2xl rounded-lg bg-muted/50 px-4 py-3 text-left text-sm text-foreground whitespace-pre-line break-words"
            data-testid="poll-description"
          >
            {poll.description}
          </p>
        )}

        {/* Lieu de l'événement */}
        {eventLocation && (
          <div className="flex items-center justify-center gap-2 mt-3 text-sm text-muted-foreground">
            <MapPin className="w-4 h-4" />
            <span>{eventLocation}</span>
          </div>
        )}

        {/* Informations sous le titre */}
        <div className="flex justify-center items-center gap-6 mt-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            <span>
              {t("results_voters_count", { count: totalParticipants })}
            </span>
          </div>
          {expiryDate && (
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              <span>
                {t("results_expires_on", {
                  date: formatExpiryDate(expiryDate),
                })}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PollHeader;
